import React, { useState } from 'react';

export default function StepReviewKeywords({ suggestions, onChange, onBack, onNext }) {
  // suggestions: { "Service Name": [{kw, state, custom}], ... }
  const [inputs, setInputs] = useState({});

  const toggle = (service, index) => {
    const updated = { ...suggestions };
    const kw = updated[service][index];
    kw.state = kw.state === 'selected' ? 'rejected' : 'selected';
    onChange(updated);
  };

  const selectAll = (service) => {
    const updated = { ...suggestions };
    updated[service] = updated[service].map(k => ({ ...k, state: 'selected' }));
    onChange(updated);
  };

  const deselectAll = (service) => {
    const updated = { ...suggestions };
    updated[service] = updated[service].map(k => ({ ...k, state: 'rejected' }));
    onChange(updated);
  };

  const addCustomKeyword = (service) => {
    const val = (inputs[service] || '').trim().toLowerCase();
    if (!val) return;
    // Prevent duplicates
    if (suggestions[service].some(k => k.kw === val)) {
      setInputs(prev => ({ ...prev, [service]: '' }));
      return;
    }
    const updated = { ...suggestions };
    updated[service] = [...updated[service], { kw: val, state: 'selected', custom: true }];
    onChange(updated);
    setInputs(prev => ({ ...prev, [service]: '' }));
  };

  const removeCustomKeyword = (service, index) => {
    const updated = { ...suggestions };
    updated[service] = updated[service].filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleKeyDown = (e, service) => {
    if (e.key === 'Enter') addCustomKeyword(service);
  };

  const totalSelected = Object.values(suggestions).flat().filter(k => k.state === 'selected').length;
  const totalAll = Object.values(suggestions).flat().length;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Review keyword suggestions</div>
        <div className="page-sub">
          Select the AI-suggested keywords to include, and add your own ideas for each service.
          All selected keywords will be combined with locations in the next step.
        </div>
      </div>

      <div className="alert alert-info">
        <strong>{totalSelected}</strong> of {totalAll} keywords selected. Click any keyword to toggle it on or off.
      </div>

      {Object.entries(suggestions).map(([service, keywords]) => {
        const selectedCount = keywords.filter(k => k.state === 'selected').length;
        const aiKeywords = keywords.filter(k => !k.custom);
        const customKeywords = keywords.filter(k => k.custom);

        return (
          <div className="card" key={service} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <div className="card-title" style={{ marginBottom: 0 }}>{service}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                  {selectedCount} of {keywords.length} selected
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm" onClick={() => selectAll(service)}>All</button>
                <button className="btn btn-sm" onClick={() => deselectAll(service)}>None</button>
              </div>
            </div>

            {/* AI suggestions */}
            <div className="section-label" style={{ marginTop: 0 }}>AI suggestions</div>
            <div className="chip-grid">
              {aiKeywords.map((k, i) => (
                <span
                  key={i}
                  className={`chip ${k.state}`}
                  onClick={() => toggle(service, keywords.indexOf(k))}
                >
                  {k.kw}
                </span>
              ))}
            </div>

            {/* Custom keywords */}
            <div className="section-label">Your own keyword ideas</div>
            <div className="chip-grid" style={{ marginBottom: 8 }}>
              {customKeywords.map((k, i) => (
                <span
                  key={i}
                  className={`chip ${k.state}`}
                  style={{ paddingRight: 6 }}
                >
                  <span onClick={() => toggle(service, keywords.indexOf(k))}>{k.kw}</span>
                  <span
                    onClick={() => removeCustomKeyword(service, keywords.indexOf(k))}
                    style={{ marginLeft: 4, opacity: 0.5, cursor: 'pointer', fontWeight: 600 }}
                  >×</span>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <input
                type="text"
                placeholder="Type a keyword and press Enter or Add…"
                value={inputs[service] || ''}
                onChange={e => setInputs(prev => ({ ...prev, [service]: e.target.value }))}
                onKeyDown={e => handleKeyDown(e, service)}
                style={{ flex: 1, maxWidth: 340 }}
              />
              <button
                className="btn btn-sm"
                onClick={() => addCustomKeyword(service)}
                disabled={!(inputs[service] || '').trim()}
              >
                Add
              </button>
            </div>
          </div>
        );
      })}

      <div className="actions">
        <button className="btn" onClick={onBack}>← Back</button>
        <button className="btn btn-primary" onClick={onNext} disabled={totalSelected === 0}>
          Next: Locations →
        </button>
      </div>
    </div>
  );
}
